/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */

import PropTypes from 'prop-types';
import React from 'react';

import { Payment } from '@mui/icons-material';
import {
  Alert,
  Button,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useColorScheme,
} from '@mui/material';
import Box from '@mui/material/Box';
import { grey } from '@mui/material/colors';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {
  groupBy,
  uniqBy,
} from 'lodash';
import moment from 'moment';
import Horse from '../../../../../../components/Horse';
import QuestriLogo from '../../../../../../components/QuestriLogo';
import TenantAvatar from '../../../../../../components/Tenant/TenantAvatar';
import {
  primary,
  secondary,
} from '../../../../../../utils/brandColors';
import { INVOICE_STATUSES } from '../../../../../../utils/enumHelper';
import {
  getQueryParam,
  removeQueryParam,
} from '../../../../../../utils/urlHelper';

function sumArray(arr) {
  let total = 0;
  for (let i = 0; i < arr.length; i += 1) {
    total += arr[i];
  }
  return total;
}
const PdfView = ({ invoice }) => {
  const [successfulPayment, setSuccessfulPayment] = React.useState(false);
  const [canceledPayment, setCanceledPayment] = React.useState(false);
  const { mode } = useColorScheme();

  React.useEffect(() => {
    setSuccessfulPayment(getQueryParam('paymentSuccess') || false)
    setCanceledPayment(getQueryParam('paymentCanceled') || false)

    removeQueryParam('paymentSuccess')
    removeQueryParam('paymentCanceled')
  }, [])

  const getSubtotal = () => {
    let total = 0;
    for (let i = 0; i < invoice.horseBillables.length; i += 1) {
      total += parseFloat(invoice.horseBillables[i].price);
    }
    return parseFloat(total).toFixed(2);
  }

  const getAmountDue = () => {
    let total = 0;
    for (let i = 0; i < invoice.horseBillables.length; i += 1) {
      total += parseFloat(invoice.horseBillables[i].price);
    }
    if (invoice.discountAmount) {
      total -= invoice.discountAmount;
    } else if (invoice.discountPercent) {
      total -= (total * (invoice.discountPercent / 100))
    }
    return parseFloat(total).toFixed(2);
  }

  const hasDiscount = (invoice.discountAmount || invoice.discountPercent);
  const groupedByType = groupBy(invoice.horseBillablesSorted, 'note');
  const groupedByHorse = groupBy(invoice.horseBillablesSorted, 'horse.id')

  return (
    <Box
      sx={{
        maxWidth: 840,
        m: 'auto',
      }}
    >
      <Paper
        sx={{ p: 2 }}
      >
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                mb: 2,
              }}
            >
              <TenantAvatar
                tenant={{
                  displayName: invoice.barn.name,
                  iconColor: primary.main,
                  avatar: ((invoice.invoiceHeader && invoice.invoiceHeader.logo && `https://${invoice.invoiceHeader.logo}`) || `https://d8adkpqcrx2wo.cloudfront.net/${invoice.barn.tenant}.png`),
                }}
                sx={{
                  width: 56,
                  height: 56,
                }}
              />
              <Box
                sx={{ ml: 2 }}
              >
                <Typography variant="h3" fontWeight={600}>
                  {invoice.title || 'Missing Title'}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mt: 2,
                  }}
                >
                  {
                    (INVOICE_STATUSES.find((s) => s.enum === (successfulPayment
                      ? 'Paid'
                      : invoice.status)) || { chip: <Chip label="Unknown Status" /> }).chip
                  }

                  {
                    invoice && (invoice.status === 'Open' || invoice.status === 'Failed') && invoice.id && invoice.barn.stripeConnectedAccountId && !successfulPayment && (
                      <form action="/create-checkout-session-connected-account" method="POST">
                        <input
                          style={{ display: 'none' }}
                          type="text"
                          id="amountDue"
                          name="amountDue"
                          value={invoice.finalAmount}
                        />
                        <input style={{ display: 'none' }} type="text" id="returnUrl" name="returnUrl" value={window.location.href} />
                        <input
                          style={{ display: 'none' }}
                          type="text"
                          id="errorUrl"
                          name="errorUrl"
                          defaultValue={`${window.location.origin}/authorization-error`}
                        />
                        <input style={{ display: 'none' }} type="text" id="invoiceTitle" name="invoiceTitle" value={invoice.title} />
                        <input style={{ display: 'none' }} type="text" id="invoiceId" name="invoiceId" value={invoice.id} />
                        <input style={{ display: 'none' }} type="text" id="tenant" name="tenant" value={invoice.barn.tenant} />
                        <input style={{ display: 'none' }} type="text" id="connectedAccountId" name="connectedAccountId" value={invoice.barn.stripeConnectedAccountId} />
                        <input
                          style={{ display: 'none' }}
                          type="text"
                          id="authorization"
                          name="authorization"
                          defaultValue={`bearer ${localStorage.id_token}`}
                        />
                        <Button startIcon={<Payment />} sx={{ ml: 2 }} variant="contained" type="submit">
                          Pay Invoice Online
                        </Button>
                      </form>
                    )
                  }
                </Box>
              </Box>
            </Box>

            {
              successfulPayment && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Your payment was successful!
                </Alert>
              )
            }
            {
              canceledPayment && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Your payment was cancled.
                </Alert>
              )
            }

            {
              invoice && invoice.status === 'Pending' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Pending invoices are awaiting final payment processing.
                  The payment was submitted but funds have not transfered (typically for ACH and Bank transfers).
                  Check back in a few days.
                </Alert>
              )
            }
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <Typography textAlign="right" variant="h4" fontWeight={600}>Invoice</Typography>
            <Typography textAlign="right" fontWeight={700}>{invoice.invoiceHeader
              ? invoice.invoiceHeader.name
              : invoice.barn.name}
            </Typography>
            <Typography textAlign="right">{invoice.invoiceHeader
              ? invoice.invoiceHeader.addressLine1
              : invoice.barn.barnMetadata.addressLine1}
            </Typography>
            {((invoice.invoiceHeader && invoice.invoiceHeader.addressLine2) || invoice.barn.barnMetadata.addressLine2) && (
              <Typography textAlign="right">{invoice.invoiceHeader
                ? invoice.invoiceHeader.addressLine2
                : invoice.barn.barnMetadata.addressLine2}
              </Typography>
            )}
            <Typography textAlign="right">
              {invoice.invoiceHeader
                ? invoice.invoiceHeader.city
                : invoice.barn.barnMetadata.city}, {invoice.invoiceHeader
                  ? invoice.invoiceHeader.stateProvinceCounty
                  : invoice.barn.barnMetadata.stateProvinceCounty} {invoice.invoiceHeader
                    ? invoice.invoiceHeader.zipOrPostalcode
                    : invoice.barn.barnMetadata.zipOrPostalcode}
            </Typography>
            {((invoice.invoiceHeader && invoice.invoiceHeader.country) || invoice.barn.barnMetadata.country) && (
              <Typography textAlign="right">{invoice.invoiceHeader
                ? invoice.invoiceHeader.country
                : invoice.barn.barnMetadata.country}
              </Typography>
            )}
            {((invoice.invoiceHeader && invoice.invoiceHeader.phone) || invoice.barn.barnMetadata.phone) && (
              <Typography textAlign="right" sx={{ mt: 1 }}>{invoice.invoiceHeader
                ? invoice.invoiceHeader.phone
                : invoice.barn.barnMetadata.phone}
              </Typography>
            )}
            {((invoice.invoiceHeader && invoice.invoiceHeader.email) || invoice.barn.barnMetadata.email) && (
              <Typography textAlign="right">{invoice.invoiceHeader
                ? invoice.invoiceHeader.email
                : invoice.barn.barnMetadata.email}
              </Typography>
            )}
          </Grid>

        </Grid>
        <Divider
          sx={{
            mt: 4,
            mb: 4,
          }}
        />
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              sm: 7,
            }}
          >
            <Typography fontWeight={600} component="div">Bill To</Typography>
            {
              invoice.barnUsers.map((bu) => (
                <Box key={`invoice-user-${bu.id}`} sx={{ mt: 1 }}>
                  <Typography component="div">{bu.userFullName}</Typography>
                  <Typography fontWeight={300}>({bu.email || 'missing email'})</Typography>
                </Box>
              ))
            }
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 5,
            }}
          >
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell align="right">Invoice ID:</TableCell>
                  <TableCell>{(invoice && invoice.id)
                    ? invoice.id
                    : <Typography variant="caption">Available after saving</Typography>}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="right">Invoice Date:</TableCell>
                  <TableCell>
                    {moment(invoice.invoiceDate).format('MM/DD/yyyy')}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell align="right">Due Date:</TableCell>
                  <TableCell>
                    {moment(invoice.dueDate).format('MM/DD/yyyy')}
                  </TableCell>
                </TableRow>
                <TableRow
                  sx={{
                    backgroundColor: mode === 'light'
                      ? grey[100]
                      : grey[900],
                  }}
                >
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Amount Due:</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    ${getAmountDue()}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Grid>
        </Grid>
        <Divider
          sx={{
            mt: 4,
            mb: 4,
          }}
        />
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 700 }} aria-label="customized table">
            <TableHead
              sx={{ backgroundColor: primary[500] }}
            >
              <TableRow>
                <TableCell sx={{ color: 'white' }}>Horse</TableCell>
                <TableCell sx={{ color: 'white' }}>Date</TableCell>
                <TableCell sx={{ color: 'white' }}>Transaction (Description)</TableCell>
                <TableCell sx={{ color: 'white' }} align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {
                !invoice.groupHorseBillablesByHorse && !invoice.groupHorseBillablesByTitle && invoice.horseBillablesSorted.map((hb) => (
                  <TableRow
                    key={`invoice-billable-${hb.id}`}
                  >
                    <TableCell component="th" scope="row">
                      <Horse horse={hb.horse} showShowName />
                    </TableCell>
                    <TableCell>
                      {moment(hb.billableDate).format('MM/DD/YYYY')}
                    </TableCell>
                    <TableCell>
                      {hb.note}{hb.quantity && ` (qty ${hb.quantity})`}
                    </TableCell>
                    <TableCell align="right"><b>${parseFloat(hb.price).toFixed(2)}</b></TableCell>
                  </TableRow>
                ))
              }
              {
                invoice.groupHorseBillablesByHorse && !invoice.groupHorseBillablesByTitle && Object.keys(groupedByHorse).map((horseId) => (
                  <TableRow key={`invoice-billable-${horseId}`}>
                    <TableCell component="th" scope="row">
                      <Horse horse={groupedByHorse[horseId][0].horse} showShowName />
                    </TableCell>
                    <TableCell>
                      {groupedByHorse[horseId].map((hb) => moment(hb.billableDate).format('MM/DD/YYYY')).join(', ')}
                    </TableCell>
                    <TableCell>{uniqBy(groupedByHorse[horseId], 'note').map((hb) => hb.note).join(', ')}</TableCell>
                    <TableCell align="right"><b>${parseFloat(sumArray(groupedByHorse[horseId].map((hb) => hb.price))).toFixed(2)}</b></TableCell>
                  </TableRow>
                ))
              }
              {
                !invoice.groupHorseBillablesByHorse && invoice.groupHorseBillablesByTitle && Object.keys(groupedByType).map((note) => (
                  <TableRow key={`invoice-billable-${note}`}>
                    <TableCell component="th" scope="row">
                      {uniqBy(groupedByType[note], 'horse.id').map((hb) => (<Horse horse={hb.horse} showShowName key={`desc-horse-${hb.id}`} />))}
                    </TableCell>
                    <TableCell>
                      {groupedByType[note].map((hb) => moment(hb.billableDate).format('MM/DD/YYYY')).join(', ')}
                    </TableCell>
                    <TableCell>{note}</TableCell>
                    <TableCell align="right"><b>${parseFloat(sumArray(groupedByType[note].map((hb) => hb.price))).toFixed(2)}</b></TableCell>
                  </TableRow>
                ))
              }
              {
                invoice.horseBillablesSorted.length === 0 && (
                  <TableRow>
                    <TableCell />
                    <TableCell><Typography fontWeight={300}>No transactions/records</Typography></TableCell>
                    <TableCell align="right" />
                  </TableRow>
                )
              }
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{ mt: 4 }}
        >
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 7,
              }}
            />
            <Grid
              size={{
                xs: 12,
                sm: 5,
              }}
            >
              <Table size="small">
                <TableBody>
                  {
                    hasDiscount && (
                      <TableRow>
                        <TableCell align="right">
                          <Box sx={{ fontWeight: 600 }}>Subtotal:</Box>
                          <Box sx={{ color: '#2e7d32' }}>{invoice.discountAmount
                            ? (invoice.discountDescription || 'Discount')
                            : `${invoice.discountPercent}% ${(invoice.discountDescription || 'Discount')}`}:
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box>${getSubtotal()}</Box>
                          <Box sx={{ color: '#2e7d32' }}>$({parseFloat(getSubtotal() - getAmountDue()).toFixed(2)})</Box>
                        </TableCell>
                      </TableRow>
                    )
                  }
                  <TableRow>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 600,
                        borderBottom: '2px solid',
                      }}
                    >Total:
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ borderBottom: '2px solid' }}
                    >
                      ${getAmountDue()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 600 }}
                    >Amount Due:
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 600 }}
                    >
                      ${getAmountDue()}
                    </TableCell>
                  </TableRow>
                  {
                    invoice.barn.stripeConnectedAccountId && (

                      <TableRow>
                        <TableCell />
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 300,
                            fontSize: 14,
                          }}
                        >
                          <i>&#42;Additional sales tax may apply</i>
                        </TableCell>
                      </TableRow>
                    )
                  }
                </TableBody>
              </Table>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mt: 4 }}>
          <Typography fontWeight={600} component="div" gutterBottom>Notes/Terms</Typography>
          <Typography>{invoice.notes || 'No invoice notes or terms provided.'}</Typography>
        </Box>
      </Paper>
      <Box
        sx={{
          backgroundColor: primary[500],
          borderBottomRightRadius: 8,
          borderBottomLeftRadius: 8,
          p: 2,
        }}
      >
        <Grid container spacing={2} justifyContent="center" alignItems="center">
          <Grid>
            <QuestriLogo iconOnly removePadding color="#fff" fontSize={35} />
          </Grid>

          <Grid>
            <Box
              sx={{
                color: secondary[300],
                fontSize: '13px',
                fontWeight: 400,
                textAlign: 'center',
              }}
            >This invoice was generated by <b><a style={{ color: 'white' }} target="blank" href="https://getquestri.com">Questri</a></b>.
            </Box>
            <Box
              sx={{
                color: secondary[300],
                fontSize: '13px',
                fontWeight: 400,
                textAlign: 'center',
              }}
            >Questri is used by equestrian businesses to manage billing, planning, and horse management.
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

PdfView.propTypes = { invoice: PropTypes.object };

PdfView.defaultProps = { invoice: {} };

export default (PdfView);
